import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {
  Text,
  Clipboard,
  StyleSheet,
  TouchableOpacity,
  View,
  Platform,
} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {MessageText, MessageImage, Time, utils, useChatContext} from 'react-native-gifted-chat';
import {isTablet} from 'react-native-device-info';

import {deleteMessage} from 'store/communication/actions/chatActions';
import {translate} from 'i18n/context/I18nContext';

const {isSameUser, isSameDay} = utils;

const MessageBubble = (props) => {
  const {
    containerStyle,
    wrapperStyle,
    messageTextStyle,
    currentMessage = {text: null, createdAt: null, image: null},
    previousMessage = {},
    nextMessage = {},
    user,
    touchableProps = {},
    onLongPress: onLongPressProp = null,
    renderMessageImage: renderMessageImageProp = null,
    renderMessageText: renderMessageTextProp = null,
    renderCustomView: renderCustomViewProp = null,
    renderUsername: renderUsernameProp,
    renderTime: renderTimeProp = null,
    renderTicks: renderTicksProp,
    tickStyle,
    usernameStyle,
    containerToNextStyle,
    containerToPreviousStyle,
  } = props;

  const dispatch = useDispatch();
  const liveTranslation = useSelector(
    (state) => state.meeting.settings.liveTranslation,
  );
  const {actionSheet} = useChatContext();

  const [showOriginalText, setShowOriginalText] = useState(false);

  const onLongPress = useCallback(() => {
    if (onLongPressProp) {
      onLongPressProp({actionSheet}, currentMessage);
    } else {
      if (currentMessage.text) {
        const options = [
          translate('messageBubble.copyText'),
          translate('messageBubble.deleteMsg'),
          translate('shared.cancel'),
        ];
        const cancelButtonIndex = options.length - 1;
        actionSheet().showActionSheetWithOptions(
          {
            options,
            cancelButtonIndex,
          },
          (buttonIndex) => {
            switch (buttonIndex) {
              case 0:
                Clipboard.setString(currentMessage.text);
                break;
              case 1:
                dispatch(deleteMessage(currentMessage));
                break;
            }
          },
        );
      }
    }
  }, [onLongPressProp, currentMessage, actionSheet, dispatch]);

  const renderMessageText = () => {
    if (renderMessageTextProp) {
      const {
        containerStyle: _cs,
        wrapperStyle: _ws,
        messageTextStyle: _mts,
        currentMessage: _cm,
        renderMessageText: _rmt,
        ...messageTextProps
      } = props;
      return renderMessageTextProp(messageTextProps);
    } else {
      const {
        containerStyle: _cs,
        wrapperStyle: _ws,
        messageTextStyle: _mts,
        currentMessage: _cm,
        renderMessageText: _rmt,
        ...messageTextProps
      } = props;
      return (
        <MessageText
          {...messageTextProps}
          currentMessage={
            typeof currentMessage?.translatedText !== 'undefined' &&
            liveTranslation
              ? {...currentMessage, text: currentMessage.translatedText}
              : currentMessage
          }
          textStyle={{
            left: [
              styles.standardFont,
              styles.messageText,
              messageTextProps.textStyle,
              messageTextStyle,
            ],
          }}
        />
      );
    }
  };

  const renderMessageImage = () => {
    if (currentMessage.image) {
      const {containerStyle: _cs, wrapperStyle: _ws, ...messageImageProps} =
        props;
      if (renderMessageImageProp) {
        return renderMessageImageProp(messageImageProps);
      }
      return (
        <MessageImage
          {...messageImageProps}
          imageStyle={[styles.image, messageImageProps.imageStyle]}
        />
      );
    }
    return null;
  };

  const renderTicks = () => {
    if (renderTicksProp) {
      return renderTicksProp(currentMessage);
    }
    if (currentMessage.user._id !== user._id) {
      return null;
    }
    if (currentMessage.sent || currentMessage.received) {
      return (
        <View style={[styles.headerItem, styles.tickView]}>
          {currentMessage.sent && (
            <Text style={[styles.standardFont, styles.tick, tickStyle]}>
              ✓
            </Text>
          )}
          {currentMessage.received && (
            <Text style={[styles.standardFont, styles.tick, tickStyle]}>
              ✓
            </Text>
          )}
        </View>
      );
    }
    return null;
  };

  const renderUsername = () => {
    const username = currentMessage.user.name;
    if (username) {
      const {containerStyle: _cs, wrapperStyle: _ws, ...usernameProps} = props;
      if (renderUsernameProp) {
        return renderUsernameProp(usernameProps);
      }
      return (
        <Text
          style={[
            styles.standardFont,
            styles.headerItem,
            styles.username,
            usernameStyle,
          ]}>
          {username}
        </Text>
      );
    }
    return null;
  };

  const renderTime = () => {
    if (currentMessage.createdAt) {
      const {containerStyle: _cs, wrapperStyle: _ws, ...timeProps} = props;
      if (renderTimeProp) {
        return renderTimeProp(timeProps);
      }
      return (
        <Time
          {...timeProps}
          containerStyle={{left: [styles.timeContainer]}}
          textStyle={{
            left: [
              styles.standardFont,
              styles.headerItem,
              styles.time,
              timeProps.textStyle,
            ],
          }}
        />
      );
    }
    return null;
  };

  const renderCustomView = () => {
    if (renderCustomViewProp) {
      return renderCustomViewProp(props);
    }
    return null;
  };

  const toggleTranslationLink = () => {
    setShowOriginalText((prev) => !prev);
  };

  const renderTranslationLink = () => {
    if (
      typeof currentMessage?.translatedText !== 'undefined' &&
      liveTranslation
    ) {
      return (
        <View>
          {showOriginalText ? (
            <Text style={styles.orginalText}>{currentMessage?.text}</Text>
          ) : null}
          <Text style={styles.linkText} onPress={toggleTranslationLink}>
            {!showOriginalText
              ? translate('liveTranslation.showOriginal')
              : translate('liveTranslation.hideOriginal')}
          </Text>
        </View>
      );
    }
    return null;
  };

  const isSameThread =
    isSameUser(currentMessage, previousMessage) &&
    isSameDay(currentMessage, previousMessage);

  const messageHeader = isSameThread ? null : (
    <View style={styles.headerView}>
      {renderUsername()}
      {renderTime()}
      {renderTicks()}
    </View>
  );

  return (
    <View style={[styles.container, containerStyle]}>
      <TouchableOpacity
        onLongPress={onLongPress}
        accessibilityTraits="text"
        {...touchableProps}>
        <View style={[styles.wrapper, wrapperStyle]}>
          <View>
            {renderCustomView()}
            {messageHeader}
            {renderMessageImage()}
            {renderMessageText()}
            {renderTranslationLink()}
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
};

// Note: Everything is forced to be "left" positioned with this component.
// The "right" position is only used in the default Bubble.
const styles = StyleSheet.create({
  standardFont: {
    fontSize: 15,
  },
  messageText: {
    width: Platform.OS === 'web' || isTablet() ? 248 : 180,
    marginLeft: 0,
    marginRight: 0,
  },
  orginalText: {
    width: Platform.OS === 'web' || isTablet() ? 200 : 160,
    marginLeft: 0,
    marginRight: 0,
    color: '#88aaaa',
  },
  container: {
    alignItems: 'flex-start',
  },
  wrapper: {
    minHeight: 20,
    justifyContent: 'flex-end',
  },
  username: {
    fontWeight: 'bold',
  },
  time: {
    textAlign: 'left',
    fontSize: 12,
  },
  timeContainer: {
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0,
  },
  headerItem: {
    marginRight: 10,
  },
  headerView: {
    // Try to align it better with the avatar on Android.
    marginTop: Platform.OS === 'android' ? -2 : 0,
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  tick: {
    backgroundColor: 'transparent',
    color: 'white',
  },
  tickView: {
    flexDirection: 'row',
  },
  linkText: {
    fontSize: 12,
    paddingRight: 10,
    textAlign: 'right',
    textDecorationLine: 'underline',
  },
  image: {
    borderRadius: 3,
    marginLeft: 0,
    marginRight: 0,
  },
});

MessageBubble.defaultProps = {
  touchableProps: {},
  onLongPress: null,
  renderMessageImage: null,
  renderMessageText: null,
  renderCustomView: null,
  renderTime: null,
  currentMessage: {
    text: null,
    createdAt: null,
    image: null,
  },
  nextMessage: {},
  previousMessage: {},
  containerStyle: {},
  wrapperStyle: {},
  tickStyle: {},
  containerToNextStyle: {},
  containerToPreviousStyle: {},
};

MessageBubble.propTypes = {
  touchableProps: PropTypes.object,
  onLongPress: PropTypes.func,
  renderMessageImage: PropTypes.func,
  renderMessageText: PropTypes.func,
  renderCustomView: PropTypes.func,
  renderUsername: PropTypes.func,
  renderTime: PropTypes.func,
  renderTicks: PropTypes.func,
  currentMessage: PropTypes.object,
  nextMessage: PropTypes.object,
  previousMessage: PropTypes.object,
  user: PropTypes.object,
  containerStyle: PropTypes.shape({
    left: PropTypes.object,
    right: PropTypes.object,
  }),
  wrapperStyle: PropTypes.shape({
    left: PropTypes.object,
    right: PropTypes.object,
  }),
  messageTextStyle: PropTypes.object,
  usernameStyle: PropTypes.object,
  tickStyle: PropTypes.object,
  containerToNextStyle: PropTypes.shape({
    left: PropTypes.object,
    right: PropTypes.object,
  }),
  containerToPreviousStyle: PropTypes.shape({
    left: PropTypes.object,
    right: PropTypes.object,
  }),
};

export default MessageBubble;
